/* eslint-disable no-unused-vars */
/**
 * Myra Audio Monitor
 *
 * A standalone class that continuously monitors microphone/audio health
 * and automatically attempts to reconnect/repair when issues are detected.
 *
 * Features:
 * - Monitors WebSocket connection health
 * - Monitors Deepgram connection status
 * - Monitors MediaStream track health
 * - Monitors audio data flow
 * - Auto-reconnects on failures
 * - Integrates with audio diagnostics for repairs
 * - Provides callbacks for UI updates
 *
 * @example
 * const monitor = new MyraAudioMonitor({
 *   websocket: ws,
 *   onStatusChange: (status) => console.log('Status:', status),
 *   onError: (error) => console.error('Error:', error),
 *   onReconnectAttempt: (attempt) => console.log('Reconnecting...', attempt),
 *   onReconnectSuccess: () => console.log('Reconnected!'),
 *   onReconnectFailed: (error) => console.error('Failed to reconnect:', error)
 * });
 *
 * // Start monitoring
 * await monitor.start();
 *
 * // Update stream when it changes
 * monitor.updateStream(newStream);
 *
 * // Stop monitoring
 * monitor.stop();
 */

class MyraAudioMonitor {
  // ============================================================================
  // CONSTANTS
  // ============================================================================

  static STATUS = {
    INACTIVE: "INACTIVE", // Not started
    INITIALIZING: "INITIALIZING", // Starting up
    HEALTHY: "HEALTHY", // Everything working
    DEGRADED: "DEGRADED", // Minor issues detected
    RECONNECTING: "RECONNECTING", // Attempting to reconnect
    FAILED: "FAILED", // Reconnection failed
  };

  static ERROR_CODES = {
    WEBSOCKET_DISCONNECTED: "WEBSOCKET_DISCONNECTED",
    DEEPGRAM_DISCONNECTED: "DEEPGRAM_DISCONNECTED",
    STREAM_INACTIVE: "STREAM_INACTIVE",
    NO_AUDIO_DATA: "NO_AUDIO_DATA",
    TRACK_ENDED: "TRACK_ENDED",
    PERMISSION_LOST: "PERMISSION_LOST",
    HARDWARE_FAILURE: "HARDWARE_FAILURE",
    MAX_RETRIES_EXCEEDED: "MAX_RETRIES_EXCEEDED",
  };

  static DEFAULTS = {
    // Health check intervals - FOCUS ON ACTIVE CONNECTIONS
    healthCheckInterval: 20000, // Passive check every 20 seconds
    activeHealthCheckInterval: 60000, // Active test every 60 seconds

    // Reconnection settings - CONSERVATIVE
    maxReconnectAttempts: 20,
    reconnectDelayMs: 5000,
    reconnectBackoffMultiplier: 1.5,

    // Connection testing
    enableActiveTesting: true,
    connectionTestTimeout: 5000,

    // Audio diagnostics - OPT-IN ONLY
    runDiagnosticsOnFailure: false,
    diagnosticsTimeout: 5000,
  };

  // ============================================================================
  // CONSTRUCTOR
  // ============================================================================

  constructor(options = {}) {
    // Required dependencies
    this.websocket = options.websocket || null;

    // Callbacks
    this.onStatusChange = options.onStatusChange || (() => { });
    this.onError = options.onError || (() => { });
    this.onReconnectAttempt = options.onReconnectAttempt || (() => { });
    this.onReconnectSuccess = options.onReconnectSuccess || (() => { });
    this.onReconnectFailed = options.onReconnectFailed || (() => { });
    this.onAudioDataReceived = options.onAudioDataReceived || (() => { });

    // Configuration
    this.config = {
      ...MyraAudioMonitor.DEFAULTS,
      ...options.config,
    };

    // user config overrides options config

    if (options.config) {
      this.config = { ...this.config, ...options.config };
    }

    // State
    this.status = MyraAudioMonitor.STATUS.INACTIVE;
    this.mediaStream = null;
    this.currentTrack = null;
    this.isMonitoring = false;
    this.reconnectAttempts = 0;
    this.startupGraceUntil = Date.now() + 15000;
    // this.timers.activeHealthCheck = null;

    // Health tracking
    this.health = {
      websocketConnected: false,
      deepgramConnected: false,
      deepgramEverConnected: false,
      streamActive: false,
      trackActive: false,
      audioDataFlowing: false,
      lastAudioTimestamp: null,
      lastHealthCheck: null,
    };

    // NEW SECTION FOR CONVERSATION AWARENESS:
    this.conversationState = {
      isInConversation: false,
      isMyraSpeaking: false,
      isWaitingForResponse: false,
      lastUserActivity: null,
      expectedSilencePeriods: ["tts_start", "tts_end", "agent_speech", "nlu_result", "personalized_complete"],
    };

    // Timers
    this.timers = {
      healthCheck: null,
      audioDataTimeout: null,
      reconnectTimeout: null,
    };

    // Bind methods
    this._handleTrackEnded = this._handleTrackEnded.bind(this);
    this._handleWebSocketMessage = this._handleWebSocketMessage.bind(this);
    this._handleWebSocketClose = this._handleWebSocketClose.bind(this);
    this._handleWebSocketError = this._handleWebSocketError.bind(this);
    this.stop = this.stop.bind(this);
    this.stopStream = this.stopStream.bind(this);
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Start monitoring audio health
   * @returns {Promise<boolean>} Success status
   */
  async start() {
    if (this.isMonitoring) {
      console.warn("[AUDIO MONITOR] Already monitoring");
      return true;
    }

    console.log("[AUDIO MONITOR] Starting...");
    this._setStatus(MyraAudioMonitor.STATUS.INITIALIZING);

    try {
      // Setup WebSocket listeners
      if (this.websocket) {
        this._setupWebSocketListeners();
      } else {
        throw new Error("WebSocket is required");
      }

      this.startupGraceUntil = Date.now() + 15000;
      this.lastDeepgramDisconnect = null;
      this.deepgramReconnectCooldown = 10000;

      // Start health checks
      this._startHealthChecks();

      this.isMonitoring = true;
      this._setStatus(MyraAudioMonitor.STATUS.HEALTHY);

      console.log("[AUDIO MONITOR] Started successfully");
      return true;
    } catch (error) {
      console.error("[AUDIO MONITOR] Failed to start:", error);
      this._setStatus(MyraAudioMonitor.STATUS.FAILED);
      this.onError({
        code: "INITIALIZATION_FAILED",
        message: "Failed to start audio monitoring",
        error: error.message,
      });
      return false;
    }
  }

  /**
   * Stop monitoring
   */
  stop() {
    console.log("[AUDIO MONITOR] Stopping...");

    this.isMonitoring = false;
    this._clearAllTimers();
    this._removeWebSocketListeners();
    this._removeTrackListeners();

    this._setStatus(MyraAudioMonitor.STATUS.INACTIVE);
    console.log("[AUDIO MONITOR] Stopped");
  }
  disable() {
    console.log("[AUDIO MONITOR] Isolating and disabling monitor");

    // 1. Set disabled flag FIRST
    this._isDisabled = true;
    this.isMonitoring = false;

    // 2. Stop ALL timers (including any hidden ones)
    this._clearAllTimers();

    // 3. Remove ALL WebSocket listeners (CRITICAL)
    this._removeWebSocketListeners();

    // 4. Remove ALL track listeners
    this._removeTrackListeners();

    // 5. Clear ANY pending reconnection timeout
    if (this.timers.reconnectTimeout) {
      clearTimeout(this.timers.reconnectTimeout);
      this.timers.reconnectTimeout = null;
    }

    this.websocket = null;
    this.mediaStream = null;
    this.currentTrack = null;

    // 7. Reset all state
    this.status = MyraAudioMonitor.STATUS.INACTIVE;
    this.reconnectAttempts = 0;

    // 8. Clear health state
    this.health = {
      websocketConnected: false,
      deepgramConnected: false,
      deepgramEverConnected: false,
      streamActive: false,
      trackActive: false,
      audioDataFlowing: false,
      lastAudioTimestamp: null,
      lastHealthCheck: null,
    };

    console.log("[AUDIO MONITOR] Monitor completely isolated - cannot reconnect");
  }

  stopStream() {
    if (!this.mediaStream) return false;

    const audioTracks = this.mediaStream.getAudioTracks();
    if (audioTracks.length === 0) return false;

    const track = audioTracks[0];
    if (track) {
      track.stop();
    }
  }

  /**
   * Update the media stream being monitored
   * @param {MediaStream} stream - New media stream
   */
  updateStream(stream) {
    console.log("[AUDIO MONITOR] Updating stream...");

    // Remove old track listeners
    this._removeTrackListeners();

    // Update stream
    this.mediaStream = stream;

    if (stream && stream.getAudioTracks().length > 0) {
      this.currentTrack = stream.getAudioTracks()[0];
      this._setupTrackListeners();

      this.health.streamActive = stream.active;
      this.health.trackActive = this.currentTrack.readyState === "live";

      console.log("[AUDIO MONITOR] Stream updated:", {
        streamActive: this.health.streamActive,
        trackActive: this.health.trackActive,
        trackLabel: this.currentTrack.label,
      });
    } else {
      console.warn("[AUDIO MONITOR] No audio tracks in stream");
      this.health.streamActive = false;
      this.health.trackActive = false;
    }

    this._checkHealth();
  }

  /**
   * Notify that audio data was received (call this when Deepgram processes audio)
   */
  notifyAudioDataReceived() {
    // updated timestamp for debugging, no health implications
    this.health.lastAudioTimestamp = Date.now();
    this.onAudioDataReceived();
  }

  /**
   * Notify that Deepgram connection status changed
   * @param {boolean} connected - Connection status
   */
  notifyDeepgramStatus(connected) {
    if (this.health.deepgramConnected === connected || this.health.deepgramConnected === true) { return; } // Already processed this, do nothing

    const wasConnected = this.health.deepgramConnected;
    this.health.deepgramConnected = connected;

    if (connected) {
      this.health.deepgramEverConnected = true;
      console.log("[AUDIO MONITOR] Deepgram connected");
    } else {
      // Just log, don't attempt reconnect
      console.log("[AUDIO MONITOR] Deepgram disconnected (backend will handle reconnection)");
    }

    this._checkHealth();
  }

  /**
   * Get current health status
   * @returns {Object} Health status
   */
  getHealth() {
    return {
      status: this.status,
      ...this.health,
      reconnectAttempts: this.reconnectAttempts,
      timeSinceLastAudio: this.health.lastAudioTimestamp ? Date.now() - this.health.lastAudioTimestamp : null,
    };
  }

  /**
   * Force a reconnection attempt
   * @returns {Promise<boolean>} Success status
   */
  async forceReconnect() {
    console.log("[AUDIO MONITOR] Force reconnect requested");
    return await this._attemptReconnect("MANUAL_TRIGGER");
  }
  /**
   * Test WebSocket connection by sending a ping
   */
  async _testWebSocketConnection() {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      return false;
    }

    return new Promise((resolve) => {
      const testId = `test-${Date.now()}`;
      const timeout = setTimeout(() => {
        this.websocket.removeEventListener("message", handleResponse);
        resolve(false);
      }, 3000);

      const handleResponse = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === "pong" || message.testId === testId) {
            clearTimeout(timeout);
            this.websocket.removeEventListener("message", handleResponse);
            resolve(true);
          }
        } catch (error) {
          // Ignore non-JSON messages
        }
      };

      this.websocket.addEventListener("message", handleResponse);

      try {
        this.websocket.send(
          JSON.stringify({
            type: "ping",
            testId: testId,
            timestamp: Date.now(),
          })
        );
      } catch (error) {
        clearTimeout(timeout);
        this.websocket.removeEventListener("message", handleResponse);
        resolve(false);
      }
    });
  }

  /**
   * Test microphone stream health
   */
  _testStreamHealth() {
    if (!this.mediaStream) return false;

    const audioTracks = this.mediaStream.getAudioTracks();
    if (audioTracks.length === 0) return false;

    const track = audioTracks[0];
    return track.readyState === "live" && this.mediaStream.active && !track.muted;
  }

  /**
   * Active health check - tests actual connections
   */
  async _performActiveHealthCheck() {
    const results = {
      websocket: await this._testWebSocketConnection(),
      stream: this._testStreamHealth(),
      deepgram: this.health.deepgramConnected, // From backend messages
      timestamp: Date.now(),
    };

    console.log("[AUDIO MONITOR] Active health check results:", results);
    return results;
  }
  // ============================================================================
  // PRIVATE: SETUP & TEARDOWN
  // ============================================================================

  _setupWebSocketListeners() {
    if (!this.websocket) return;

    this.websocket.addEventListener("message", this._handleWebSocketMessage);
    this.websocket.addEventListener("close", this._handleWebSocketClose);
    this.websocket.addEventListener("error", this._handleWebSocketError);

    this.health.websocketConnected = this.websocket.readyState === WebSocket.OPEN;
  }

  _removeWebSocketListeners() {
    if (!this.websocket) return;

    this.websocket.removeEventListener("message", this._handleWebSocketMessage);
    this.websocket.removeEventListener("close", this._handleWebSocketClose);
    this.websocket.removeEventListener("error", this._handleWebSocketError);
  }

  _setupTrackListeners() {
    if (!this.currentTrack) return;

    this.currentTrack.addEventListener("ended", this._handleTrackEnded);
  }

  _removeTrackListeners() {
    if (!this.currentTrack) return;

    this.currentTrack.removeEventListener("ended", this._handleTrackEnded);
  }

  _startHealthChecks() {
    this._clearAllTimers();

    // Passive health check (lightweight)
    this.timers.healthCheck = setInterval(() => {
      this._checkHealth();
    }, this.config.healthCheckInterval);

    // Active health check (comprehensive but less frequent)
    if (this.config.enableActiveTesting) {
      this.timers.activeHealthCheck = setInterval(async () => {
        await this._performActiveHealthCheck();
      }, this.config.activeHealthCheckInterval);
    }
  }

  _resetAudioDataTimeout() {
    console.warn("_resetAudioDataTimeout called");
    if (this.timers.audioDataTimeout) {
      clearTimeout(this.timers.audioDataTimeout);
      this.timers.audioDataTimeout = null;
    }
  }

  _clearAllTimers() {
    if (this.timers.healthCheck) {
      clearInterval(this.timers.healthCheck);
      this.timers.healthCheck = null;
    }

    if (this.timers.audioDataTimeout) {
      clearTimeout(this.timers.audioDataTimeout);
      this.timers.audioDataTimeout = null;
    }

    if (this.timers.reconnectTimeout) {
      clearTimeout(this.timers.reconnectTimeout);
      this.timers.reconnectTimeout = null;
    }
  }

  // ============================================================================
  // PRIVATE: EVENT HANDLERS
  // ============================================================================

  _handleWebSocketMessage(event) {
    // ANY MESSAGE MEANS CONNECTION IS WORKING - no audio flow implications

    try {
      const message = JSON.parse(event.data);
      if (message.type === "tts_start" || message.type === "agent_speech") {
        this.conversationState.isMyraSpeaking = true;
        this.conversationState.lastUserActivity = Date.now();
      } else if (message.type === "tts_end") {
        this.conversationState.isMyraSpeaking = false;
      } else if (message.type === "nlu_result") {
        this.conversationState.isWaitingForResponse = false;
        this.conversationState.lastUserActivity = Date.now();
      } else if (message.type === "tts_listening") {
        this.conversationState.isWaitingForResponse = true;
      }

      // Track Deepgram status from backend messages
      if (message.type === "deepgram_connected") {
        this.notifyDeepgramStatus(true);
      } else if (message.type === "deepgram_disconnected") {
        this.notifyDeepgramStatus(false);
      }
      // No conversation state tracking needed - we only care about connections
    } catch (error) {
      // Binary data means connection is working
    }
  }

  _handleWebSocketClose(event) {
    if (this._isDisabled) return;

    console.warn("[AUDIO MONITOR] WebSocket closed:", event.code, event.reason);
    this.health.websocketConnected = false;
    this._handleFailure(MyraAudioMonitor.ERROR_CODES.WEBSOCKET_DISCONNECTED);
  }

  _handleWebSocketError(event) {
    if (this._isDisabled) return;

    console.error("[AUDIO MONITOR] WebSocket error:", event);
    this.health.websocketConnected = false;
    this._handleFailure(MyraAudioMonitor.ERROR_CODES.WEBSOCKET_DISCONNECTED);
  }

  _handleTrackEnded() {
    console.warn("[AUDIO MONITOR] Audio track ended");
    this.health.trackActive = false;
    this._handleFailure(MyraAudioMonitor.ERROR_CODES.TRACK_ENDED);
  }

  _handleDeepgramDisconnect() {
    console.log("[AUDIO MONITOR] Deepgram disconnected (may be temporary)");

    const now = Date.now();
    const timeSinceLast = this.lastDeepgramDisconnect ? now - this.lastDeepgramDisconnect : Infinity;

    // IMPORTANT: Don't attempt reconnect if:
    // 1. We're in a conversation (Myra is speaking or we're waiting for response)
    // 2. It's been less than 10 seconds since last disconnect (cooldown)
    // 3. We're already attempting to reconnect

    const shouldReconnect =
      !this.conversationState.isMyraSpeaking &&
      !this.conversationState.isWaitingForResponse &&
      timeSinceLast > 10000 && // 10 second cooldown (was 30s)
      this.status !== MyraAudioMonitor.STATUS.RECONNECTING;

    if (shouldReconnect) {
      console.warn("[AUDIO MONITOR] Deepgram disconnected - attempting reconnect after cooldown");
      this.lastDeepgramDisconnect = now;
      this._handleFailure(MyraAudioMonitor.ERROR_CODES.DEEPGRAM_DISCONNECTED);
    } else {
      // Just log and wait - backend will likely reconnect automatically
      console.log("[AUDIO MONITOR] Deepgram disconnect ignored (normal/cooldown/in conversation)");
    }
  }

  _handleAudioDataTimeout() {
    // ✅ NO-OP - We don't monitor audio flow anymore
    console.log("[AUDIO MONITOR] Audio timeout - This is normal during conversation");
  }

  // ============================================================================
  // PRIVATE: HEALTH CHECKS
  // ============================================================================

  _checkHealth() {
    if (!this.isMonitoring) return;

    if (Date.now() < this.startupGraceUntil || !this.health.deepgramEverConnected) {
      return; // absolutely no reconnects during startup
    }

    this.health.lastHealthCheck = Date.now();

    // Update real-time status
    if (this.websocket) {
      this.health.websocketConnected = this.websocket.readyState === WebSocket.OPEN;
    }

    if (this.mediaStream) {
      this.health.streamActive = this.mediaStream.active;
    }

    if (this.currentTrack) {
      this.health.trackActive = this.currentTrack.readyState === "live";
    }

    // Check for issues - ONLY CRITICAL ONES
    const issues = [];

    if (!this.health.websocketConnected) {
      issues.push("WebSocket disconnected");
    }

    if (!this.health.streamActive) {
      issues.push("Stream inactive");
    }

    if (!this.health.trackActive) {
      issues.push("Track inactive");
    }

    if (this.health.deepgramEverConnected && !this.health.deepgramConnected) {
      const timeSinceLastDisconnect = this.lastDeepgramDisconnect
        ? Date.now() - this.lastDeepgramDisconnect
        : 30000;
      if (timeSinceLastDisconnect < this.deepgramReconnectCooldown) {
        console.log(`[AUDIO MONITOR] Deepgram disconnect cooldown active (${Math.round((this.deepgramReconnectCooldown - timeSinceLastDisconnect) / 1000)}s remaining)`);
        return; // Skip reconnect during cooldown
      }
      issues.push("Deepgram disconnected");
      this.lastDeepgramDisconnect = Date.now();
    }

    // COMPLETELY REMOVE AUDIO DATA FLOW CHECKING
    // This was causing the infinite loop

    // Only reconnect for critical connection issues
    const criticalIssues = issues.filter((issue) => issue.includes("WebSocket") || issue.includes("Deepgram") || issue.includes("Stream inactive"));

    if (criticalIssues.length > 0) {
      console.warn("[AUDIO MONITOR] Critical issues detected:", criticalIssues);
      this._handleFailure(MyraAudioMonitor.ERROR_CODES.WEBSOCKET_DISCONNECTED);
    }

    // Update status based on issues
    if (issues.length === 0) {
      if (this.status !== MyraAudioMonitor.STATUS.HEALTHY) {
        this._setStatus(MyraAudioMonitor.STATUS.HEALTHY);
      }
    } else if (issues.length > 0 && this.status === MyraAudioMonitor.STATUS.HEALTHY) {
      this._setStatus(MyraAudioMonitor.STATUS.DEGRADED);
      console.warn("[AUDIO MONITOR] Health degraded:", issues);
    }

    // Log health periodically (less frequently)
    if (Math.random() < 0.02) {
      //  (2% of checks)
      console.log("[AUDIO MONITOR] Health check:", {
        status: this.status,
        criticalIssues: criticalIssues.length > 0 ? criticalIssues : "none",
        allIssues: issues.length > 0 ? issues : "none",
        timeSinceAudio: this.health.lastAudioTimestamp ? `${Math.round((Date.now() - this.health.lastAudioTimestamp) / 1000)}s` : "never",
      });
    }
  }

  // ============================================================================
  // PRIVATE: FAILURE HANDLING & RECONNECTION
  // ============================================================================

  async _handleFailure(errorCode) {
    console.error("[AUDIO MONITOR] Failure detected:", errorCode);
    if (this._isDisabled || !this.isMonitoring) {
      console.log("[AUDIO MONITOR] Monitor disabled/not monitoring, ignoring failure");
      return;
    }
    // Notify error
    this.onError({
      code: errorCode,
      message: this._getErrorMessage(errorCode),
      health: this.getHealth(),
    });

    // Don't attempt reconnect if we're not monitoring
    if (!this.isMonitoring) {
      return;
    }

    // Don't attempt reconnect if already reconnecting
    if (this.status === MyraAudioMonitor.STATUS.RECONNECTING) {
      console.log("[AUDIO MONITOR] Already reconnecting, skipping...");
      return;
    }

    // Attempt reconnection
    await this._attemptReconnect(errorCode);
  }

  async _attemptReconnect(errorCode) {
    if (this._isDisabled || !this.isMonitoring) {
      console.log("[AUDIO MONITOR] Monitor disabled/not monitoring, skipping reconnect");
      return false;
    }
    if (!this.isMonitoring) {
      console.log("[AUDIO MONITOR] Not monitoring, skipping reconnect");
      return false;
    }

    // Check if we've exceeded max attempts
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      console.error("[AUDIO MONITOR] Max reconnect attempts exceeded");
      this._setStatus(MyraAudioMonitor.STATUS.FAILED);

      this.onReconnectFailed({
        code: MyraAudioMonitor.ERROR_CODES.MAX_RETRIES_EXCEEDED,
        message: "Failed to reconnect after maximum attempts",
        attempts: this.reconnectAttempts,
        originalError: errorCode,
      });

      return false;
    }

    this.reconnectAttempts++;
    this._setStatus(MyraAudioMonitor.STATUS.RECONNECTING);

    console.log(`[AUDIO MONITOR] Reconnect attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts}`);

    this.onReconnectAttempt({
      attempt: this.reconnectAttempts,
      maxAttempts: this.config.maxReconnectAttempts,
      originalError: errorCode,
    });

    // Calculate backoff delay
    const delay = this.config.reconnectDelayMs * Math.pow(this.config.reconnectBackoffMultiplier, this.reconnectAttempts - 1);

    console.log(`[AUDIO MONITOR] Waiting ${delay}ms before reconnect...`);
    await new Promise((resolve) => setTimeout(resolve, delay));

    // Step 1: Run audio diagnostics if enabled

    // if (this.config.runDiagnosticsOnFailure) {
    //   console.log("[AUDIO MONITOR] Running audio diagnostics...");

    //   try {
    //     // Assuming diagnoseAndFixAudio is available globally or imported
    //     const diagnosticResult = await window.MyraAudioDiagnostics.diagnoseAndFixAudio({
    //       timeout: this.config.diagnosticsTimeout,
    //       verbose: true,
    //     });

    //     if (diagnosticResult.success) {
    //       console.log("[AUDIO MONITOR] Diagnostics successful, updating stream...");

    //       // Update with new stream
    //       this.updateStream(diagnosticResult.stream);

    //       // Reset reconnect counter
    //       this.reconnectAttempts = 0;
    //       this._setStatus(MyraAudioMonitor.STATUS.HEALTHY);

    //       this.onReconnectSuccess({
    //         attempt: this.reconnectAttempts,
    //         method: "diagnostics",
    //         stream: diagnosticResult.stream,
    //       });

    //       return true;
    //     } else {
    //       console.warn("[AUDIO MONITOR] Diagnostics failed:", diagnosticResult.code);

    //       // If diagnostics failed with a user-action-required error, don't retry
    //       const nonRetryableErrors = ["PERMISSION_DENIED", "NO_MICROPHONE", "BROWSER_NOT_SUPPORTED", "INSECURE_CONTEXT"];

    //       if (nonRetryableErrors.includes(diagnosticResult.code)) {
    //         console.error("[AUDIO MONITOR] Non-retryable error, stopping reconnection");
    //         this._setStatus(MyraAudioMonitor.STATUS.FAILED);

    //         this.onReconnectFailed({
    //           code: diagnosticResult.code,
    //           message: diagnosticResult.message,
    //           requiresUserAction: true,
    //         });

    //         return false;
    //       }
    //     }
    //   } catch (error) {
    //     console.error("[AUDIO MONITOR] Diagnostics exception:", error);
    //   }
    // }

    // Step 2: Simple reconnect attempt (without diagnostics)
    console.log("[AUDIO MONITOR] Attempting simple reconnect...");

    try {

      if (window.audioPlayer && window.audioPlayer._checkAndRecoverAudioContext) {
        await window.audioPlayer._checkAndRecoverAudioContext();
      }

      // Try to get a new stream
      // const stream = await navigator.mediaDevices.getUserMedia({
      //   audio: {
      //     channelCount: 1,
      //     echoCancellation: true,
      //     noiseSuppression: false,
      //     autoGainControl: true,
      //     sampleRate: 48000
      //   },
      // });

      // console.log("[AUDIO MONITOR] Got new stream");
      // this.updateStream(stream);

      // Send message to backend to restart Deepgram
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.send(
          JSON.stringify({
            type: "restart_deepgram",
          })
        );
      }

      // Reset reconnect counter
      this.reconnectAttempts = 0;
      this._setStatus(MyraAudioMonitor.STATUS.HEALTHY);

      // this.onReconnectSuccess({
      //   attempt: this.reconnectAttempts,
      //   method: "simple",
      //   stream: stream,
      // });

      return true;
    } catch (error) {
      console.error("[AUDIO MONITOR] Simple reconnect failed:", error);

      // Try again
      return await this._attemptReconnect(errorCode);
    }
  }

  // ============================================================================
  // PRIVATE: UTILITIES
  // ============================================================================

  _setStatus(newStatus) {
    if (this.status === newStatus) return;

    const oldStatus = this.status;
    this.status = newStatus;

    console.log(`[AUDIO MONITOR] Status: ${oldStatus} → ${newStatus}`);

    this.onStatusChange({
      oldStatus,
      newStatus,
      timestamp: Date.now(),
    });
  }

  _getErrorMessage(errorCode) {
    const messages = {
      [MyraAudioMonitor.ERROR_CODES.WEBSOCKET_DISCONNECTED]: "WebSocket connection lost",
      [MyraAudioMonitor.ERROR_CODES.DEEPGRAM_DISCONNECTED]: "Deepgram connection lost",
      [MyraAudioMonitor.ERROR_CODES.STREAM_INACTIVE]: "Media stream became inactive",
      [MyraAudioMonitor.ERROR_CODES.NO_AUDIO_DATA]: "No audio data received",
      [MyraAudioMonitor.ERROR_CODES.TRACK_ENDED]: "Audio track ended",
      [MyraAudioMonitor.ERROR_CODES.PERMISSION_LOST]: "Microphone permission lost",
      [MyraAudioMonitor.ERROR_CODES.HARDWARE_FAILURE]: "Hardware failure detected",
      [MyraAudioMonitor.ERROR_CODES.MAX_RETRIES_EXCEEDED]: "Maximum reconnection attempts exceeded",
    };

    return messages[errorCode] || "Unknown error";
  }
}

// ============================================================================
// EXPORT
// ============================================================================

if (typeof module !== "undefined" && module.exports) {
  module.exports = MyraAudioMonitor;
}

if (typeof window !== "undefined") {
  window.MyraAudioMonitor = MyraAudioMonitor;
}

// UI Helper functions (implement these in your application)

export function updateUIStatus(status) {
  // Update your UI to show connection status
}

export function showErrorNotification(message) {
  // Show error to user
}

export function showReconnectingUI(attempt, maxAttempts) {
  // Show "Reconnecting... (1/3)" message
}

export function hideReconnectingUI() {
  // Hide reconnecting message
}

export function showSuccessMessage(message) {
  // Show success notification
}

export function showFailureMessage(message) {
  // Show failure notification
}

export function showUserActionRequired(code, message) {
  // Show user that they need to take action
  // Use the MYRA_AUDIO_MESSAGES from the previous artifact
}

export default MyraAudioMonitor;
